import React from "react";
import { useFela } from "react-fela";
import useTranslation from "next-translate/useTranslation";

import { IWithCustomStyle, useTheme } from "@/hooks/useTheme";
import { Grid } from "@/components/Grid";
import { ListElement } from "@/components/ListElement";
import { Heading } from "@/components/Heading";
import { Image } from "@/components/Image";
import { LinkButton } from "@/components/LinkButton";

import { IShareDesignSection } from "./types";

type IProps = IShareDesignSection &
  JSX.IntrinsicElements["div"] &
  IWithCustomStyle;

export const ShareDesignSection: React.FC<IProps> = ({
  elements,
  title,
  image,
  backgroundColor = "#f9f9f9",
  cta,
  onButtonClick,
  ...rest
}) => {
  const { css } = useFela();
  const { bp, colors } = useTheme();
  const { t } = useTranslation();

  return (
    <Grid
      customStyle={{
        position: "relative",
        flexDirection: "column",
        [bp.FROM_DESKTOP]: {
          flexDirection: "row",
          justifyContent: "space-between",
        },
      }}
      wrapperStyle={{ backgroundColor }}
      id="ShareDesignSection"
      {...rest}
    >
      <div
        className={css({
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: 60,
          [bp.FROM_DESKTOP]: { alignItems: "flex-start", marginRight: 120 },
        })}
      >
        <Heading as="h2">{title}</Heading>

        {elements.length && (
          <ul
            className={css({
              margin: 0,
              padding: 0,
              listStyleType: "none",
            })}
          >
            {elements.map(({ id, title, text, iconType }) => (
              <ListElement
                key={`ListElement-${id}`}
                id={`ListElement-${id}`}
                title={title}
                text={text}
                iconType={iconType}
                textStyle={{
                  fontSize: 18,
                  lineHeight: "26px",
                  letterSpacing: "0.5px",
                  color: colors.GRAY,
                }}
              />
            ))}
          </ul>
        )}
        <LinkButton
          title={t("common:everythingWeSupport")}
          href="/converter-features"
          customStyle={{
            width: "auto",
            paddingTop: 40,
            color: colors.ORANGE,
            backgroundColor: "none",
            border: "none",
            nested: {
              "> svg": {
                marginLeft: 23,
                nested: {
                  "> path": {
                    stroke: colors.ORANGE,
                  },
                },
              },
            },
          }}
          icon="ARROW-RIGHT"
        />
        {cta && (
          <LinkButton customStyle={{ width: "auto", marginTop: 45 }} {...cta} />
        )}
      </div>
      <div
        className={css({
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        })}
      >
        <div
          className={css({
            width: "100%",
            marginBottom: 20,
            [bp.ONLY_TABLET]: {
              width: "60%",
            },
            [bp.FROM_DESKTOP]: {
              margin: "0 auto",
            },
          })}
        >
          <Image
            customStyle={{
              maxWidth: "100%",
              margin: "0 auto",
              boxShadow: "7px 7px 11px 0 rgba(16,5,48,0.08)",
              borderRadius: 8,
            }}
            src={image.src}
            alt={image.alt}
          />
        </div>
      </div>
    </Grid>
  );
};
